import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Tag } from '../api';
import { useFilters } from '../contexts/FilterContext';
import { Icon } from '../icons';

// The header above Ideas, Board and Mind map (P3-19): title, count, the active
// filters as removable chips, then search and the Tags / Status / Links menus.
// These controls lived in the sidebar in 1.0. The similarity slider is gone:
// meaning search uses the fixed default (0.20); a per-account threshold moves to
// Settings › Data › Matching (D-81, P3-08).

const STATUSES = ['captured', 'exploring', 'building', 'shipped', 'shelved'] as const;
const STATUS_LABEL: Record<string, string> = {
  captured: 'Captured', exploring: 'Exploring', building: 'Building', shipped: 'Shipped', shelved: 'Shelved',
};

const LINK_TYPES = [
  { id: 'related', label: 'Related' },
  { id: 'parent', label: 'Parent' },
  { id: 'blocks', label: 'Blocks' },
  { id: 'inspires', label: 'Inspires' },
  { id: 'duplicate', label: 'Duplicate' },
  { id: 'supersedes', label: 'Supersedes' },
];

type SearchHow = 'semantic' | 'or' | 'and' | 'exact';
const SEARCH_HOW: { id: SearchHow; label: string }[] = [
  { id: 'semantic', label: 'By meaning' },
  { id: 'or', label: 'Any word' },
  { id: 'and', label: 'All words' },
  { id: 'exact', label: 'Exact phrase' },
];

/** A toolbar button that opens a small checklist menu; closes on outside click or Escape. */
function Menu({ label, count, children }: { label: string; count: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="tb-menu" ref={ref}>
      <button className={`tb-btn${count > 0 ? ' tb-btn-on' : ''}`} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}
        {count > 0 && <span className="tb-btn-count">{count}</span>}
        <Icon name="chevron" size={12} className="tb-caret" />
      </button>
      {open && <div className="tb-pop" role="group" aria-label={label}>{children}</div>}
    </div>
  );
}

export function ViewToolbar({ title, count, tags, showLinks = true }: {
  title: string;
  count?: number;
  tags: Tag[];
  /** The mind map has its own Links menu (P3-10), which holds the link-type filter there. */
  showLinks?: boolean;
}) {
  const f = useFilters();
  const [tagQuery, setTagQuery] = useState('');
  const how: SearchHow = f.searchMode === 'semantic' ? 'semantic' : f.ftsMode;
  const shownTags = tags.filter((t) => t.name.toLowerCase().includes(tagQuery.toLowerCase()));

  const setHow = (next: SearchHow) => {
    if (next === 'semantic') f.setSearchMode('semantic');
    else {
      f.setSearchMode('keyword');
      f.setFtsMode(next);
    }
  };

  const chips = [
    ...f.selectedTags.map((t) => ({ key: `t:${t}`, k: 'Tag', v: t, remove: () => f.removeTag(t) })),
    ...f.selectedStatuses.map((s) => ({ key: `s:${s}`, k: 'Status', v: STATUS_LABEL[s] ?? s, remove: () => f.toggleStatus(s) })),
    ...f.selectedConnectionTypes.map((c) => ({
      key: `c:${c}`, k: 'Link', v: LINK_TYPES.find((l) => l.id === c)?.label ?? c, remove: () => f.toggleConnectionType(c),
    })),
  ];

  return (
    <header className="view-toolbar">
      <h1 className="tb-title">{title}</h1>
      {count !== undefined && <span className="tb-count">{count}</span>}
      {chips.map((c) => (
        <button key={c.key} className="tb-chip" onClick={c.remove} aria-label={`Remove filter ${c.k} ${c.v}`}>
          <span className="tb-chip-k">{c.k}</span>{c.v}<Icon name="close" size={12} />
        </button>
      ))}
      {chips.length > 1 && <button className="tb-link" onClick={f.clearFilters}>Clear all</button>}

      <span className="tb-spacer" />

      <div className="tb-search">
        <Icon name="search" size={14} />
        <input
          type="search"
          className="toolbar-search-input"
          placeholder="Search ideas"
          aria-label="Search ideas"
          value={f.searchQuery}
          onChange={(e) => f.setSearchQuery(e.target.value)}
        />
        <select className="tb-search-how" aria-label="How to match" value={how} onChange={(e) => setHow(e.target.value as SearchHow)}>
          {SEARCH_HOW.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
        </select>
      </div>

      <Menu label="Tags" count={f.selectedTags.length}>
        <input
          className="tb-pop-search"
          placeholder="Find a tag"
          aria-label="Find a tag"
          value={tagQuery}
          onChange={(e) => setTagQuery(e.target.value)}
          autoFocus
        />
        <div className="tb-pop-list">
          {shownTags.map((t) => (
            <label key={t.id} className="tb-pop-item">
              <input type="checkbox" checked={f.selectedTags.includes(t.name)} onChange={() => f.toggleTag(t.name)} />
              <span className="tb-pop-name">{t.name}</span>
              <span className="tb-pop-meta">{t.idea_count}</span>
            </label>
          ))}
          {shownTags.length === 0 && <span className="tb-pop-empty">No tags match</span>}
        </div>
      </Menu>

      <Menu label="Status" count={f.selectedStatuses.length}>
        <div className="tb-pop-list">
          {STATUSES.map((s) => (
            <label key={s} className="tb-pop-item">
              <input type="checkbox" checked={f.selectedStatuses.includes(s)} onChange={() => f.toggleStatus(s)} />
              <span className="tb-dot" style={{ background: `var(--nr-status-${s})` }} aria-hidden="true" />
              <span className="tb-pop-name">{STATUS_LABEL[s]}</span>
            </label>
          ))}
        </div>
      </Menu>

      {showLinks && <Menu label="Links" count={f.selectedConnectionTypes.length}>
        <div className="tb-pop-list">
          {LINK_TYPES.map((l) => (
            <label key={l.id} className="tb-pop-item">
              <input type="checkbox" checked={f.selectedConnectionTypes.includes(l.id)} onChange={() => f.toggleConnectionType(l.id)} />
              <span className="tb-pop-name">{l.label}</span>
            </label>
          ))}
        </div>
      </Menu>}
    </header>
  );
}
